import { useTranslation } from "react-i18next"
import { GraphQLTaggedNode, useMutation, UseMutationConfig } from "react-relay"
import { MutationParameters, PayloadError } from "relay-runtime"
import { extractGraphqlErrors } from "@/lib/utils"
import { ConfirmationPropsWithoutOpen, useConfirmStore } from "@/stores/useConfirmStore"

interface CustomMutationParams {
  confirmProps?: ConfirmationPropsWithoutOpen
  shouldShowErrorDialog?: boolean
  considerErrorAsBlocking?: (errorTypeName: string) => boolean
}

// The purpose of this custom mutation hook is to execute the mutation with enhanced error handling
function useMutationWithErrorDialog<T extends MutationParameters>(
  mutation: GraphQLTaggedNode,
  params?: CustomMutationParams,
): [(config: UseMutationConfig<T>) => void, boolean] {
  const [commit, isInFlight] = useMutation<T>(mutation)
  const confirm = useConfirmStore((state) => state.confirm)
  const { t } = useTranslation()

  const { shouldShowErrorDialog = true, confirmProps = {}, considerErrorAsBlocking = () => true } = { ...params }

  const showErrorDialog = () => {
    confirm({
      title: t("patient.dialog.error.unableToSaveChanges"),
      content: "patient.dialog.error.tryAgainLater",
      cancelText: "common.button.close",
      isShowConfirmButton: false,
      ...confirmProps,
    })
  }

  const customCommit = (config: UseMutationConfig<T>) => {
    const configWithHandling = {
      ...config,
      onCompleted: (response: T["response"], graphqlErrors: PayloadError[] | null) => {
        if (graphqlErrors?.length) {
          if (shouldShowErrorDialog) showErrorDialog()

          // Call original onError if exists
          config.onError?.(new Error(graphqlErrors[0].message))
          return
        }

        const errors = extractGraphqlErrors(response)
        if (errors.length > 0) {
          if (errors.some((e) => !("__typename" in e) || considerErrorAsBlocking(e.__typename))) {
            if (shouldShowErrorDialog) showErrorDialog()

            // Call original onError if exists
            config.onError?.(new Error(errors[0]?.message))
            return
          }
        }

        // Call original onCompleted if exists
        config.onCompleted?.(response, graphqlErrors)
      },
      onError: (error: Error) => {
        if (shouldShowErrorDialog) showErrorDialog()

        // Call original onError if exists
        config.onError?.(error)
      },
    }

    commit(configWithHandling)
  }

  return [customCommit, isInFlight]
}

export { useMutationWithErrorDialog, type CustomMutationParams }
